import React from 'react';
import { Alert } from '@trussworks/react-uswds';
import ExternalLink from '@components/external-link/ExternalLink';
import LinkButton from '@components/link/linkButton';
import { NprVariant, NprInstructionsConfig } from '@src/app-pages/Forms/registry/configs/npr/nprConfig';
import { getAppStore } from '@src/utils/store-reference';
import { FileTypes, ResourceFiles, RequestType } from '@src/utils/enums';
import { useUserRoleCapabilities } from '@src/stores/userRoleCapabilities.store';

export const getNprInstructionsV1 = (args: { variant: NprVariant; onJdLink: () => void }): NprInstructionsConfig => {
  const { variant, onJdLink } = args;
  const store = getAppStore();
  const canCreateRequest = useUserRoleCapabilities.getState().hasRoleCapability('createRequest');
  const isAbNpr = variant !== 'npr';
  const nprText = isAbNpr ? RequestType.ActivityBasedNoPermitRequired : 'NPR';

  return {
    headerText: 'INSTRUCTIONS',
    metadataHeader: isAbNpr ? RequestType.ActivityBasedNoPermitRequired : RequestType.NoPermitRequired,
    paragraphs: [
      <p key='p1'>
        A Department of the Army (DA) permit may not be required for certain activities in waters of the United States,
        particularly when the activity is not regulated, is exempt under Section 404(f) of the Clean Water Act (CWA),
        and is not recaptured (
        <ExternalLink
          href='https://www.ecfr.gov/current/title-33/part-323#p-323.4(c)'
          title='33 CFR 323.4(c)'
          content='33 CFR 323.4(c)'
        />
        ). In such cases, a{' '}
        {isAbNpr ? RequestType.ActivityBasedNoPermitRequired : `${RequestType.NoPermitRequired} (NPR)`} letter may be
        provided to document that a DA permit is not necessary. This {nprText} request offers applicants an opportunity
        to enter project-specific details that help inform whether or not the specified activity requires a permit from
        the USACE Regulatory office.
      </p>,

      <Alert key='a1' heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
        If you believe there are no streams or wetlands in your project area which are subject to USACE regulation, you
        may{' '}
        <LinkButton
          onClick={onJdLink}
          title='Submit a Jurisdictional Determination request'
          content='submit a Jurisdictional Determination request'
          unstyled
          disabled={!canCreateRequest}
          href={undefined}
          children={undefined}
          className={undefined}
        />
        . An {nprText} letter cannot be used to determine or address geographic jurisdiction. Only a Jurisdictional
        Determination (JD) can establish whether waters or wetlands on a site are subject to regulatory jurisdiction
        under the CWA or Rivers and Harbors Act of 1899.
      </Alert>,

      <p key='p2' className='margin-top-2'>
        While an {nprText} letter is not required, this request type has been created to provided an automated response
        letter that indicates when permit is not required for the specified activity.
      </p>,

      <Alert key='a2' heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
        If you’re unfamiliar with the USACE Regulatory Program and/or local USACE District Regulatory procedures, we
        recommend that you request a pre-application meeting with the local USACE District prior to submitting this
        request. You may also{' '}
        <ExternalLink
          href='https://regulatory.ops.usace.army.mil/offices/'
          title='Contact Info'
          content='contact your local USACE District'
        />{' '}
        to ask general questions and to determine how to proceed.
      </Alert>,

      <p key='p3' className='margin-top-2'>
        If an activity will alter, occupy or use a U.S. Army Corps of Engineers federally authorized civil works
        project, review and permission must be obtained from USACE pursuant to 33 U.S.C. 408. In such cases, submittals
        must include a statement confirming that the project proponent has submitted a written request for Section 408
        permission from, or review by, the District office having jurisdiction over that USACE project. To learn more,
        visit the{' '}
        <ExternalLink
          href='https://www.usace.army.mil/Missions/Civil-Works/Section408/'
          title='HQ 408 web page'
          content='HQ 408 web page.'
        />
      </p>,

      <p key='p4'>
        The US Coast Guard regulates bridges over navigable waters of the US under Section 9 of the Rivers and Harbors
        Act of 1899. If working on a bridge over a navigable water, please contact the US Coast Guard. There may be
        components of the bridge that still require USACE review under Section 404.
      </p>,

      <div key='d1' className='margin-top-2'>
        <LinkButton
          onClick={() => store.doDownloadFile(ResourceFiles.Reg101FreqUsedTerms, FileTypes.Resource)}
          title='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          content='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          href={undefined}
          children={undefined}
          className={undefined}
        />
      </div>,
    ],
  };
};
